// Dev-Mode: a developer/tester toggle that is only offered off production.
//
// When ACTIVE it (a) forces the Anyline "Zählerstand scannen" buttons to show
// regardless of device/eligibility, and (b) reveals the OCR confidence after a
// scan. See MeterReadingView.vue for the wiring.
//
// State is a module-level singleton (shared across every component that calls
// useDevMode) and persisted to localStorage so the toggle survives reloads
// during a test session. `available` gates everything on the environment, so a
// stale enabled=true from a TEST session can never light up dev affordances if
// the same browser later loads PROD.

import { computed, ref, watch } from "vue";
import { isProdEnv } from "./ene";

const STORAGE_KEY = "ene.devMode";

function readPersisted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// Singleton state — declared once at module load, shared by all callers.
const enabled = ref(readPersisted());

watch(
  enabled,
  (on) => {
    try {
      localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
    } catch {
      /* localStorage unavailable (private mode / SSR) — ignore */
    }
  },
  { flush: "sync" }
);

export function useDevMode() {
  /** Whether the Dev-Mode switch may be shown at all (dev + staging, not prod). */
  const available = computed(() => !isProdEnv());
  /** The switch is both available AND turned on. The only flag features should
   *  check — guards against a persisted enabled=true leaking onto prod. */
  const active = computed(() => available.value && enabled.value);
  return { available, enabled, active };
}
